import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SITE } from "@/constants/testIds";
import {
  RefreshCw,
  ArrowLeft,
  Check,
  Trash2,
  Undo2,
  Lock,
  LogOut,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCE_LABEL = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  learning: "Learn Something New",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/* ---------------- Login screen ---------------- */
function LoginScreen() {
  const { login } = useAuth();
  const [form, setForm] = useState({ username: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(form.username.trim(), form.password);
      toast.success(`Welcome back, ${result?.username || form.username.trim()}.`);
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) ||
        err?.message ||
        "Could not sign in.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid={SITE.adminRoot}
      className="flex min-h-screen items-center justify-center bg-brand-cream px-6 py-12"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-brand-evergreen/70 transition hover:text-brand-evergreen"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <div className="overflow-hidden rounded-3xl bg-white p-10 shadow-[0_25px_60px_-30px_rgba(35,66,53,0.35)] ring-1 ring-brand-evergreen/10">
          <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-brand-evergreen/10 text-brand-evergreen">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="font-display text-3xl text-brand-evergreen">
            Admin sign-in
          </h1>
          <p className="mt-2 text-sm text-brand-charcoal/60">
            Please enter your credentials to review inquiries and testimonials.
          </p>
          <form
            data-testid={SITE.adminLoginForm}
            onSubmit={submit}
            className="mt-8 space-y-5"
          >
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                Username
              </span>
              <input
                data-testid={SITE.adminLoginUsername}
                type="text"
                value={form.username}
                onChange={update("username")}
                autoComplete="username"
                required
                className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                Password
              </span>
              <input
                data-testid={SITE.adminLoginPassword}
                type="password"
                value={form.password}
                onChange={update("password")}
                autoComplete="current-password"
                required
                className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
              />
            </label>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid={SITE.adminLoginSubmit}
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-evergreen px-7 py-3.5 text-sm font-medium tracking-wide text-brand-cream transition-all duration-300 hover:bg-brand-sand hover:text-brand-evergreen disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

/* ---------------- Contact submissions ---------------- */
function ContactPanel({ authHeaders, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/contact`, {
        headers: authHeaders,
      });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) return onUnauthorized();
      setError(e?.message || "Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onUnauthorized]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-brand-charcoal/70">
          {items.length} submission{items.length === 1 ? "" : "s"}
        </p>
        <Button
          data-testid={SITE.adminRefresh}
          onClick={fetchItems}
          size="sm"
          className="gap-2 bg-brand-evergreen text-brand-cream hover:bg-brand-sand hover:text-brand-evergreen"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-brand-sand/60 bg-white shadow-sm">
        <table data-testid={SITE.adminTable} className="w-full text-left text-sm">
          <thead className="bg-brand-evergreen/5 text-brand-evergreen">
            <tr>
              <th className="px-5 py-3 font-medium">Received</th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Phone</th>
              <th className="px-5 py-3 font-medium">Message</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-brand-charcoal/60">
                  No submissions yet.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr
                key={item.id}
                data-testid={SITE.adminRow(item.id)}
                className="border-t border-brand-sand/40 align-top"
              >
                <td className="whitespace-nowrap px-5 py-4 text-xs text-brand-charcoal/60">
                  {formatDate(item.created_at)}
                </td>
                <td className="px-5 py-4 font-medium text-brand-evergreen">
                  {item.name}
                </td>
                <td className="px-5 py-4 text-brand-charcoal/80">
                  <a
                    href={`mailto:${item.email}`}
                    className="hover:text-brand-evergreen hover:underline"
                  >
                    {item.email}
                  </a>
                </td>
                <td className="px-5 py-4 text-brand-charcoal/80">
                  {item.phone || "—"}
                </td>
                <td className="max-w-md px-5 py-4 text-brand-charcoal/80">
                  <p className="whitespace-pre-wrap break-words">{item.message}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Bookings ---------------- */
function BookingsPanel({ authHeaders, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/bookings`, {
        headers: authHeaders,
      });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) return onUnauthorized();
      setError(e?.message || "Could not load bookings");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onUnauthorized]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-brand-charcoal/70">
          {items.length} request{items.length === 1 ? "" : "s"}
        </p>
        <Button
          data-testid={SITE.adminRefreshBookings}
          onClick={fetchItems}
          size="sm"
          className="gap-2 bg-brand-evergreen text-brand-cream hover:bg-brand-sand hover:text-brand-evergreen"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-brand-sand/60 bg-white shadow-sm">
        <table
          data-testid={SITE.adminBookingsTable}
          className="w-full text-left text-sm"
        >
          <thead className="bg-brand-evergreen/5 text-brand-evergreen">
            <tr>
              <th className="px-5 py-3 font-medium">Received</th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Contact</th>
              <th className="px-5 py-3 font-medium">Prefers</th>
              <th className="px-5 py-3 font-medium">Level</th>
              <th className="px-5 py-3 font-medium">Group</th>
              <th className="px-5 py-3 font-medium">Message</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={7}
                  className="px-5 py-10 text-center text-brand-charcoal/60"
                >
                  No session requests yet.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr
                key={item.id}
                data-testid={SITE.adminBookingRow(item.id)}
                className="border-t border-brand-sand/40 align-top"
              >
                <td className="whitespace-nowrap px-5 py-4 text-xs text-brand-charcoal/60">
                  {formatDate(item.created_at)}
                </td>
                <td className="px-5 py-4 font-medium text-brand-evergreen">
                  {item.name}
                </td>
                <td className="px-5 py-4 text-brand-charcoal/80">
                  <a
                    href={`mailto:${item.email}`}
                    className="block hover:text-brand-evergreen hover:underline"
                  >
                    {item.email}
                  </a>
                  <a
                    href={`tel:${item.phone}`}
                    className="mt-0.5 block text-xs text-brand-charcoal/60 hover:text-brand-evergreen"
                  >
                    {item.phone}
                  </a>
                </td>
                <td className="px-5 py-4 text-brand-charcoal/80">
                  <span className="inline-flex rounded-full bg-brand-forest/10 px-2.5 py-0.5 text-xs capitalize text-brand-forest">
                    {item.contact_preference || "—"}
                  </span>
                </td>
                <td className="px-5 py-4 text-brand-charcoal/80">
                  <span className="inline-flex rounded-full bg-brand-sand/50 px-2.5 py-0.5 text-xs text-brand-evergreen">
                    {EXPERIENCE_LABEL[item.experience_level] || item.experience_level}
                  </span>
                </td>
                <td className="px-5 py-4 text-brand-charcoal/80">
                  {item.group_size}
                </td>
                <td className="max-w-md px-5 py-4 text-brand-charcoal/80">
                  <p className="whitespace-pre-wrap break-words">
                    {item.message || "—"}
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Testimonials moderation ---------------- */
function TestimonialsPanel({ authHeaders, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleErr = useCallback(
    (e, fallback) => {
      if (e?.response?.status === 401) {
        onUnauthorized();
        return;
      }
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          fallback
      );
    },
    [onUnauthorized]
  );

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/testimonials`, {
        headers: authHeaders,
      });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) return onUnauthorized();
      setError(e?.message || "Could not load testimonials");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onUnauthorized]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const approve = async (id) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/testimonials/${id}/approve`,
        null,
        { headers: authHeaders }
      );
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
      toast.success("Testimonial approved — now visible on the site.");
    } catch (e) {
      handleErr(e, "Could not approve");
    }
  };

  const unapprove = async (id) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/testimonials/${id}/unapprove`,
        null,
        { headers: authHeaders }
      );
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
      toast.success("Testimonial hidden from the public site.");
    } catch (e) {
      handleErr(e, "Could not unapprove");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: authHeaders,
      });
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Testimonial deleted.");
    } catch (e) {
      handleErr(e, "Could not delete");
    }
  };

  const pendingCount = items.filter((i) => !i.approved).length;
  const approvedCount = items.length - pendingCount;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <span className="rounded-full bg-brand-sand/50 px-3 py-1 text-brand-evergreen">
            {pendingCount} pending
          </span>
          <span className="rounded-full bg-brand-forest/10 px-3 py-1 text-brand-forest">
            {approvedCount} approved
          </span>
        </div>
        <Button
          data-testid={SITE.adminRefreshTestimonials}
          onClick={fetchItems}
          size="sm"
          className="gap-2 bg-brand-evergreen text-brand-cream hover:bg-brand-sand hover:text-brand-evergreen"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div data-testid={SITE.adminTestimonialsList} className="grid gap-4">
        {items.length === 0 && !loading && (
          <div className="rounded-2xl border border-dashed border-brand-evergreen/20 bg-white/50 p-10 text-center text-sm text-brand-charcoal/60">
            No testimonials yet.
          </div>
        )}
        {items.map((t) => (
          <article
            key={t.id}
            data-testid={SITE.adminTestimonialRow(t.id)}
            className={`rounded-2xl border bg-white p-6 shadow-sm ring-1 transition ${
              t.approved
                ? "border-brand-forest/20 ring-brand-forest/10"
                : "border-brand-sand ring-brand-sand/40"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-xl text-brand-evergreen">
                    {t.name}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider ${
                      t.approved
                        ? "bg-brand-forest/10 text-brand-forest"
                        : "bg-brand-sand/60 text-brand-evergreen"
                    }`}
                  >
                    {t.approved ? "Approved" : "Pending"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-brand-charcoal/50">
                  Submitted {formatDate(t.created_at)}
                  {t.approved && t.approved_at
                    ? ` · approved ${formatDate(t.approved_at)}`
                    : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {t.approved ? (
                  <Button
                    data-testid={SITE.adminUnapproveBtn(t.id)}
                    onClick={() => unapprove(t.id)}
                    size="sm"
                    variant="outline"
                    className="gap-2"
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    Unapprove
                  </Button>
                ) : (
                  <Button
                    data-testid={SITE.adminApproveBtn(t.id)}
                    onClick={() => approve(t.id)}
                    size="sm"
                    className="gap-2 bg-brand-forest text-brand-cream hover:bg-brand-evergreen"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Approve
                  </Button>
                )}
                <Button
                  data-testid={SITE.adminDeleteTestimonialBtn(t.id)}
                  onClick={() => remove(t.id)}
                  size="sm"
                  variant="outline"
                  className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            </div>
            <blockquote className="mt-5 whitespace-pre-wrap border-l-2 border-brand-sand pl-4 font-display text-lg italic leading-relaxed text-brand-charcoal/85">
              &ldquo;{t.message}&rdquo;
            </blockquote>
          </article>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Dashboard (authenticated shell) ---------------- */
function Dashboard() {
  const { auth, authHeaders, logout } = useAuth();

  const onUnauthorized = useCallback(() => {
    toast.error("Your session has expired. Please sign in again.");
    logout();
  }, [logout]);

  return (
    <main
      data-testid={SITE.adminRoot}
      className="min-h-screen bg-brand-cream px-6 py-12 md:px-12"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Link
              to="/"
              className="mb-3 inline-flex items-center gap-1.5 text-sm text-brand-evergreen/70 transition hover:text-brand-evergreen"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
            <h1 className="font-display text-4xl text-brand-evergreen sm:text-5xl">
              Admin
            </h1>
            <p className="mt-2 max-w-xl text-sm text-brand-charcoal/70">
              Signed in as{" "}
              <span className="font-medium text-brand-evergreen">
                {auth?.username}
              </span>
              . Review contact inquiries and moderate student testimonials.
            </p>
          </div>
          <Button
            data-testid={SITE.adminLogout}
            variant="outline"
            className="gap-2"
            onClick={logout}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-3 bg-brand-sand/40">
            <TabsTrigger
              data-testid={SITE.adminTabBookings}
              value="bookings"
              className="data-[state=active]:bg-brand-evergreen data-[state=active]:text-brand-cream"
            >
              Bookings
            </TabsTrigger>
            <TabsTrigger
              data-testid={SITE.adminTabContact}
              value="contact"
              className="data-[state=active]:bg-brand-evergreen data-[state=active]:text-brand-cream"
            >
              Contact inquiries
            </TabsTrigger>
            <TabsTrigger
              data-testid={SITE.adminTabTestimonials}
              value="testimonials"
              className="data-[state=active]:bg-brand-evergreen data-[state=active]:text-brand-cream"
            >
              Testimonials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            <BookingsPanel
              authHeaders={authHeaders}
              onUnauthorized={onUnauthorized}
            />
          </TabsContent>

          <TabsContent value="contact" className="mt-8">
            <ContactPanel
              authHeaders={authHeaders}
              onUnauthorized={onUnauthorized}
            />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-8">
            <TestimonialsPanel
              authHeaders={authHeaders}
              onUnauthorized={onUnauthorized}
            />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

/* ---------------- Page ---------------- */
export default function Admin() {
  const { isAuthenticated, checking } = useAuth();

  if (checking) {
    return (
      <main
        data-testid={SITE.adminRoot}
        className="flex min-h-screen items-center justify-center bg-brand-cream text-brand-evergreen"
      >
        <Loader2 className="h-6 w-6 animate-spin" />
      </main>
    );
  }

  return isAuthenticated ? <Dashboard /> : <LoginScreen />;
}
